import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown, Menu, X, Globe, ArrowRight,
  Settings, FileText, Layers, Building2, Briefcase,
  Cpu, MessageSquare
} from 'lucide-react';
import Cookies from 'js-cookie';

const NAV_LINKS = [
  {
    key: 'solutions',
    icon: Layers,
    href: '/solutions',
    children: [
      { key: 'boq', href: '/solutions/boq', icon: FileText },
      { key: 'submittals', href: '/solutions/submittals', icon: Settings },
    ],
  },
  {
    key: 'platform',
    icon: Cpu,
    href: '/platform',
  },
  {
    key: 'industries',
    icon: Building2,
    href: '/industries',
  },
  {
    key: 'sme',
    icon: Briefcase,
    href: '/sme-solutions',
  },
  {
    key: 'about',
    icon: MessageSquare,
    href: '/about',
  },
];

export default function SiteNavbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isRTL = i18n.language === 'ar';

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const dropdownTimerRef = useRef(null);

  const isLoggedIn = !!Cookies.get('authToken');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const toggleLanguage = () => {
    const next = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(next);
  };

  const handleMouseEnter = (key) => {
    clearTimeout(dropdownTimerRef.current);
    setActiveDropdown(key);
  };
  const handleMouseLeave = () => {
    dropdownTimerRef.current = setTimeout(() => setActiveDropdown(null), 120);
  };

  const isActive = (href) =>
    location.pathname === href || location.pathname.startsWith(href + '/');

  return (
    <>
      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-md border-b border-border-light'
            : 'bg-midnight'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-18">

            {/* ── Logo ── */}
            <button
              onClick={() => navigate('/landing')}
              className="flex items-center gap-2 flex-shrink-0"
            >
              <img src="/tuly.png" alt="Tuly Technology" className="h-8 w-auto" />
              <span className={`font-bold text-lg tracking-tight ${scrolled ? 'text-heading' : 'text-white'}`}>
                Tuly Technology
              </span>
            </button>

            {/* ── Desktop Nav ── */}
            <div className="hidden lg:flex items-center gap-1">
              {NAV_LINKS.map((link) => (
                <div
                  key={link.key}
                  className="relative nav-item"
                  onMouseEnter={() => link.children && handleMouseEnter(link.key)}
                  onMouseLeave={link.children ? handleMouseLeave : undefined}
                >
                  <button
                    onClick={() => navigate(link.href)}
                    className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive(link.href)
                        ? 'text-primary bg-primary/8'
                        : scrolled
                        ? 'text-body hover:text-primary hover:bg-primary/5'
                        : 'text-white/85 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {t(`nav.${link.key}`)}
                    {link.children && (
                      <ChevronDown
                        size={14}
                        className={`transition-transform duration-200 ${activeDropdown === link.key ? 'rotate-180' : ''}`}
                      />
                    )}
                  </button>

                  {/* Dropdown */}
                  {link.children && (
                    <AnimatePresence>
                      {activeDropdown === link.key && (
                        <motion.div
                          initial={{ opacity: 0, y: -8, scale: 0.97 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -8, scale: 0.97 }}
                          transition={{ duration: 0.18, ease: 'easeOut' }}
                          className={`absolute top-full mt-1 bg-white rounded-xl shadow-card-hover border border-border-light py-2 min-w-[220px] ${
                            isRTL ? 'right-0' : 'left-0'
                          }`}
                          onMouseEnter={() => handleMouseEnter(link.key)}
                          onMouseLeave={handleMouseLeave}
                        >
                          {link.children.map((child) => (
                            <button
                              key={child.key}
                              onClick={() => { navigate(child.href); setActiveDropdown(null); }}
                              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                                isActive(child.href)
                                  ? 'text-primary bg-primary/5 font-medium'
                                  : 'text-body hover:text-primary hover:bg-primary/4'
                              } ${isRTL ? 'text-right' : 'text-left'}`}
                            >
                              <child.icon size={15} className="text-muted flex-shrink-0" />
                              {t(`nav.${child.key}`)}
                              {isActive(child.href) && (
                                <ArrowRight size={13} className={`ml-auto text-primary ${isRTL ? 'rotate-180' : ''}`} />
                              )}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>
              ))}
            </div>

            {/* ── Right Controls ── */}
            <div className="hidden lg:flex items-center gap-2">
              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  scrolled
                    ? 'text-body hover:text-primary hover:bg-primary/5'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                <Globe size={15} />
                {t('nav.language')}
              </button>

              {isLoggedIn ? (
                <button
                  onClick={() => navigate('/home')}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-white hover:bg-primary-hover transition-colors"
                >
                  {t('nav.getStarted')}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/login')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      scrolled
                        ? 'text-body hover:text-primary hover:bg-primary/5'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {t('nav.signIn')}
                  </button>
                  <button
                    onClick={() => navigate('/contact')}
                    className="px-4 py-2 rounded-lg text-sm font-semibold bg-accent text-[#1A1A1A] hover:bg-accent-hover transition-all duration-200 shadow-sm"
                  >
                    {t('nav.bookDemo')}
                  </button>
                </>
              )}
            </div>

            {/* ── Mobile Hamburger ── */}
            <div className="flex lg:hidden items-center gap-2">
              <button onClick={toggleLanguage} className={`p-2 rounded-lg transition-colors ${scrolled ? 'text-body' : 'text-white'}`}>
                <Globe size={18} />
              </button>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className={`p-2 rounded-lg transition-colors ${scrolled ? 'text-heading' : 'text-white'}`}
              >
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>

          </div>
        </div>

        {/* ── Mobile Menu ── */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              className="lg:hidden bg-white border-t border-border-light overflow-hidden"
            >
              <div className="px-4 py-4 space-y-1">
                {NAV_LINKS.map((link) => (
                  <div key={link.key}>
                    <button
                      onClick={() => {
                        if (link.children) {
                          setMobileExpanded(mobileExpanded === link.key ? null : link.key);
                        } else {
                          navigate(link.href);
                          setMobileOpen(false);
                        }
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive(link.href) ? 'text-primary bg-primary/8' : 'text-body hover:text-primary hover:bg-primary/4'
                      } ${isRTL ? 'text-right' : 'text-left'}`}
                    >
                      {t(`nav.${link.key}`)}
                      {link.children && (
                        <ChevronDown size={14} className={`transition-transform ${mobileExpanded === link.key ? 'rotate-180' : ''}`} />
                      )}
                    </button>

                    <AnimatePresence>
                      {link.children && mobileExpanded === link.key && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className={`overflow-hidden ${isRTL ? 'pr-4' : 'pl-4'}`}
                        >
                          {link.children.map((child) => (
                            <button
                              key={child.key}
                              onClick={() => { navigate(child.href); setMobileOpen(false); }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-body hover:text-primary hover:bg-primary/4 rounded-lg transition-colors ${isRTL ? 'text-right' : 'text-left'}`}
                            >
                              <child.icon size={14} className="text-muted" />
                              {t(`nav.${child.key}`)}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}

                {/* Mobile CTA */}
                <div className="pt-3 border-t border-border-light flex flex-col gap-2">
                  {isLoggedIn ? (
                    <button onClick={() => navigate('/home')} className="w-full py-2.5 rounded-lg text-sm font-semibold bg-primary text-white">
                      {t('nav.getStarted')}
                    </button>
                  ) : (
                    <>
                      <button onClick={() => { navigate('/login'); setMobileOpen(false); }} className="w-full py-2.5 rounded-lg text-sm font-medium text-body border border-border-light">
                        {t('nav.signIn')}
                      </button>
                      <button onClick={() => { navigate('/contact'); setMobileOpen(false); }} className="w-full py-2.5 rounded-lg text-sm font-semibold bg-accent text-[#1A1A1A]">
                        {t('nav.bookDemo')}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Spacer so content doesn't hide under fixed nav */}
      <div className="h-16 lg:h-18" />
    </>
  );
}
